import React from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { themes, ExpCustomFields, IExpCustomFields, ISEO, IPost } from './interfaces';
import ROUTES, { pathPrefix } from './routes';

export const contWidthClases = 'col-12 col-lg-8 col-xl-8';

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];
export const isMobileAgent = () => {
  const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent.toLowerCase() : '';
  const mobileKeywords =
    /(android|avantgo|blackberry|bolt|boost|cricket|docomo|fone|hiptop|mini|mobi|palm|phone|pie|tablet|up\.browser|up\.link|webos|wos)/i;
  return mobileKeywords.test(userAgent);
};
export const isAndroidAgent = () => {
  const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent.toLowerCase() : '';
  const androidKeywords = /android/i;
  return androidKeywords.test(userAgent);
};
export const isIosAgent = () => {
  const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent.toLowerCase() : '';
  const iosKeywords = /(ipad|iphone)/i;
  return iosKeywords.test(userAgent);
};

export const isInternalLink = (url: string): boolean => {
  if (!url) return false;

  const trimmedUrl = url.trim();
  if (trimmedUrl === '') return false;
  return (url.startsWith(pathPrefix) || url.startsWith('/')) && !url.includes('//');
};

export const sanitizeToPlainText = (html: string) => {
  if (typeof window !== 'undefined') {
    const sanitizedHtml = DOMPurify.sanitize(html);
    const temporaryElement = document.createElement('div');
    temporaryElement.innerHTML = sanitizedHtml;
    return temporaryElement.textContent || temporaryElement.innerText || '';
  }

  const parsedHtml = parse(html);
  if (Array.isArray(parsedHtml)) {
    // Loop over the array and get the text
    const text = parsedHtml.reduce((acc, item) => {
      if (typeof item === 'string') {
        return acc + item;
      }
      if (typeof item === 'object' && 'props' in item) {
        return acc + item.props.children;
      }
      return acc;
    }, '');
    return typeof text === 'string' ? text : '';
  }

  if (typeof parsedHtml === 'object' && 'props' in parsedHtml) {
    const text = parsedHtml.props.children;
    return typeof text === 'string' ? text : '';
  }

  return typeof parsedHtml === 'string' ? parsedHtml : '';
};
export const returnTexturedClass = (theme: themes, textured: boolean = true) => {
  if (textured) {
    return theme === 'white' || theme === 'coconut'
      ? 'light-theme-textured-bg'
      : 'dark-theme-textured-bg';
  }
  return '';
};
export const returnThemeBg = (theme: themes) => {
  switch (theme) {
    case 'white':
      return 'bg-white';
    case 'coconut':
      return 'bg-coconut';
    case 'dark':
      return 'bg-midnight-blue text-white';
    default:
      return '';
  }
};

export function getPageType(path: string) {
  const isHome = path === ROUTES.HOME.toUrl();
  const isCategory = path.includes('/category/');
  const isAuthor = path.includes('/author/');
  const isSingle = path.includes(pathPrefix) && !isCategory && !isAuthor && !isHome;

  return {
    isHome,
    isCategory,
    isAuthor,
    isSingle
  };
}

export function getUniId(id: number) {
  const pageType = getPageType(window.location.pathname);
  let curId = '';
  if (pageType.isHome) {
    curId = '-c0';
  } else if (pageType.isCategory) {
    curId = '-c' + id;
  } else if (pageType.isAuthor) {
    curId = '-a' + id;
  } else if (pageType.isSingle) {
    curId = '-s' + id;
  }

  return curId;
}

export const excludeCategoryMap = {
  stgWp: {
    app: 1076,
    crm: 1423,
    regulatory: 1742,
    mobile: 1720,
    'mobile-credit-cards': 1721,
    'mobile-insurance': 1730,
    'mobile-loans': 1732,
    'new-to-credit': null,
    'savings-center': 1741,
    'score-guide': 1356,
    calculators: 1396,
    'ecom-api': 2368,
    uncategorized: 1,
    lp: 2375,
    'sandbox-not-in-prod': 14334
  },
  prodWp: {
    'ecom-api': 2393,
    lp: 2479
  }
};

export const excludeContentByCategorySlug = [
  'app',
  'crm',
  'regulatory',
  'mobile',
  'mobile-credit-cards',
  'mobile-insurance',
  'mobile-loans',
  'new-to-credit',
  'savings-center',
  'score-guide',
  'calculators',
  'ecom-api',
  'uncategorized',
  'lp',
  'sandbox-not-in-prod'
];

export const excludeContentByStageCategoryId = () => {
  const stageWp = excludeCategoryMap.stgWp;
  return Object.values(stageWp).filter((id) => id !== null);
};

export const excludeContentByProdCategoryId = () => {
  const prodWp = { ...excludeCategoryMap.stgWp, ...excludeCategoryMap.prodWp };
  return Object.values(prodWp).filter((id) => id !== null);
};

export const excludeAuthorsById = [
  186, 187, 188, 130, 183, 1, 184, 157, 156, 171, 175, 172, 92, 87, 144
];
export const configExcludedCategories = [2393];
export const configExcludeTypes = [
  'Tag',
  'PostToTagConnection',
  'TagConnection',
  'MediaItem',
  'MediaItemConnection',
  'Comment',
  'CommentConnection',
  'ContentType',
  'ContentTypeConnection',
  'Taxonomy',
  'TaxonomyConnection',
  'Menu',
  'MenuConnection',
  'MenuItem',
  'MenuItemConnection',
  'Page',
  'PageConnection',
  'PostFormat',
  'PostFormatConnection',
  'PostToPostFormatConnection',
  'PostToCommentConnection',
  'UserRole',
  'UserRoleConnection',
  'UserToCommentConnection',
  'UserToPageConnection',
  'HierarchicalContentNode',
  'HierarchicalContentNodeConnection',
  'UserToUserRoleConnection'
];
export const categorySlugsWithScoreSearch = ['improve-credit', 'score-advice', 'credit-advice'];
export function generateExcludedTypesConfig(excludedTypes: string[] = configExcludeTypes) {
  return excludedTypes.reduce(
    (acc, type) => {
      acc[type] = { exclude: true };
      return acc;
    },
    {} as Record<string, { exclude: boolean }>
  );
}

export const getTemplateAndScore = (post: IPost) => {
  let templateName: string = '';
  let score = 0;
  const postTemplate = post?.template?.templateName;
  // Determine which template to use
  if (postTemplate === 'Calculator') {
    templateName = 'calculators';
  } else if (postTemplate === 'Score Guide') {
    const scoreNumField = post.expCustomFields?.find(
      (field) => field.key === ExpCustomFields.ScoreNum
    );
    score = scoreNumField ? parseFloat(scoreNumField.value) : 0;
    templateName = 'score';
  } else if (postTemplate === 'SEM Emerging' || postTemplate === 'CRM Emerging') {
    templateName = 'sem';
  } else {
    templateName = 'default';
  }
  return { templateName, score };
};

export const truncateText = (text: string, limit: number) => {
  const cleanText = sanitizeToPlainText(text); // Cast cleanText as string
  if (!cleanText || cleanText.length <= limit) {
    return cleanText;
  }
  return cleanText.substring(0, limit) + '...';
};

export function getCustomFieldValue(metaKey: ExpCustomFields, postMeta: IExpCustomFields[]) {
  const meta = postMeta?.find((item) => item?.key === metaKey);
  return meta ? meta.value : null;
}

export function setSeoMetaTags(seo: ISEO) {
  const { og, twitter } = seo;
  const ogTitle = og?.title ? parse(og.title).toString() : null;
  const ogDescription = og?.description ? parse(og.description).toString() : null;
  const ogUrl = og?.url ? `${process.env.EXP_HOST}${pathPrefix}${og.url}` : '';
  const ogImage = og?.image || '';
  const ogType = og?.type || '';
  const twitterTitle = twitter?.title ? parse(twitter.title).toString() : null;
  const twitterDescription = twitter?.description ? parse(twitter.description).toString() : null;
  const twitterImage = twitter?.image || '';
  const twitterCard = twitter?.card || '';
  const twitterSite = twitter?.site || '@experian';
  const seoData = {
    ogTitle: {
      property: 'og:title',
      content: ogTitle
    },
    ogDescription: {
      property: 'og:description',
      content: ogDescription
    },
    ogUrl: {
      property: 'og:url',
      content: ogUrl
    },
    ogImage: {
      property: 'og:image',
      content: ogImage
    },
    ogType: {
      property: 'og:type',
      content: ogType
    },
    twitterTitle: {
      name: 'twitter:title',
      content: twitterTitle
    },
    twitterDescription: {
      name: 'twitter:description',
      content: twitterDescription
    },
    twitterImage: {
      name: 'twitter:image',
      content: twitterImage
    },
    twitterCard: {
      name: 'twitter:card',
      content: twitterCard
    },
    twitterSite: {
      name: 'twitter:site',
      content: twitterSite
    }
  };

  return Object.entries(seoData).map(([key, value]) => {
    if (value.content && value.content !== '') {
      return (
        <meta
          key={key}
          {...{ ...value, content: value.content ?? undefined }}
        />
      );
    }
    return null;
  });
}
export const getAllParams = () => {
  if (typeof window === 'undefined') return {};
  const queryString = window.location.search;
  // Create a URLSearchParams object
  const params = new URLSearchParams(queryString);
  // Convert URLSearchParams to an object
  return Object.fromEntries(params.entries());
};

export function removeDuplicatedPosts(toExcludeDuplicates: IPost[], postsArray: IPost[]): IPost[] {
  const arrayIds = new Set(postsArray.map((post) => post.databaseId));
  return toExcludeDuplicates.filter((post) => !arrayIds.has(post.databaseId));
}

export const truncateContent = (htmlContent: React.ReactNode, maxLength = 500, buffer = 10) => {
  // Helper function to extract text content from React nodes
  const extractText = (node: React.ReactNode): string => {
    if (typeof node === 'string') {
      return node;
    }
    if (React.isValidElement(node) && node.props.children) {
      return React.Children.toArray(node.props.children)
        .map(extractText)
        .join('')
        .replace(/\s+/g, ' ');
    }
    return '';
  };

  // Helper function to find optimal truncation point
  const findTruncationPoint = (text: string, maxLen: number, buf: number): number => {
    if (text.length <= maxLen + buf) {
      return text.length;
    }

    let truncateAt = maxLen;

    // Look for period within buffer range
    for (let i = maxLen; i < Math.min(maxLen + buf, text.length); i++) {
      if (text[i] === '.') {
        truncateAt = i + 1;
        break;
      }
    }

    // If no period found, find last space
    if (truncateAt === maxLen) {
      const lastSpace = text.lastIndexOf(' ', maxLen);
      if (lastSpace > maxLen * 0.8) {
        // Don't truncate too early
        truncateAt = lastSpace;
      }
    }

    return truncateAt;
  };

  // Helper function to clone React element with new children
  const cloneElementWithChildren = (
    element: React.ReactElement,
    newChildren: React.ReactNode
  ): React.ReactNode => {
    if (!React.isValidElement(element)) {
      return element;
    }

    // Check if newChildren is empty or undefined
    if (!newChildren || (Array.isArray(newChildren) && newChildren.length === 0)) {
      return null;
    }

    return React.cloneElement(element, {}, newChildren);
  };

  // Helper function to truncate a single node
  const truncateNode = (
    node: React.ReactNode,
    remainingLength: number
  ): {
    truncated: React.ReactNode | null;
    remainder: React.ReactNode | null;
    wasTruncated: boolean;
  } => {
    if (typeof node === 'string') {
      const truncateAt = findTruncationPoint(node, remainingLength, buffer);
      const truncated = node.slice(0, truncateAt);
      const remainder = node.slice(truncateAt);

      return {
        truncated: truncated.trim(),
        remainder: remainder.trim(),
        wasTruncated: remainder.length > 0
      };
    }

    if (React.isValidElement(node) && node.props.children) {
      const children = React.Children.toArray(node.props.children);
      let accumulatedLength = 0;
      let truncatedChildren = [];
      let remainderChildren: React.ReactNode[] = [];
      let wasTruncated = false;

      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const childText = extractText(child);

        if (accumulatedLength + childText.length <= remainingLength && !wasTruncated) {
          truncatedChildren.push(child);
          accumulatedLength += childText.length;
        } else if (!wasTruncated) {
          const childRemainingLength = remainingLength - accumulatedLength;
          const childResult = truncateNode(child, childRemainingLength);

          if (childResult.truncated) {
            truncatedChildren.push(childResult.truncated);
          }

          if (childResult.remainder) {
            remainderChildren.push(childResult.remainder);
          }

          remainderChildren = remainderChildren.concat(children.slice(i + 1));
          wasTruncated = true;
        } else {
          remainderChildren.push(child);
        }
      }

      return {
        truncated:
          truncatedChildren.length > 0 ? cloneElementWithChildren(node, truncatedChildren) : null,
        remainder:
          remainderChildren.length > 0 ? cloneElementWithChildren(node, remainderChildren) : null,
        wasTruncated
      };
    }

    return {
      truncated: node,
      remainder: null,
      wasTruncated: false
    };
  };

  // Main truncation logic
  const processContent = () => {
    const contentArray = React.Children.toArray(htmlContent);
    const totalText = contentArray.map(extractText).join('').replace(/\s+/g, ' ').trim();

    // Check if truncation is needed
    if (totalText.length <= maxLength + buffer) {
      return {
        truncatedContent: htmlContent,
        remainingContent: null,
        isTruncated: false,
        truncationInfo: null
      };
    }

    let accumulatedLength = 0;
    let truncatedNodes = [];
    let remainingNodes: React.ReactNode[] = [];
    let truncationInfo = null;

    for (let i = 0; i < contentArray.length; i++) {
      const node = contentArray[i];
      const nodeText = extractText(node);

      if (accumulatedLength + nodeText.length <= maxLength) {
        truncatedNodes.push(node);
        accumulatedLength += nodeText.length;
      } else {
        const remainingLength = maxLength - accumulatedLength;
        const result = truncateNode(node, remainingLength);

        if (result.truncated) {
          truncatedNodes.push(result.truncated);
        }

        // Store truncation info for proper reconstruction
        truncationInfo = {
          nodeIndex: i,
          originalNode: node,
          truncatedPart: result.truncated,
          remainderPart: result.remainder
        };

        // Add remainder of current node and all subsequent nodes
        if (result.remainder) {
          remainingNodes.push(result.remainder);
        }
        remainingNodes = remainingNodes.concat(contentArray.slice(i + 1));
        break;
      }
    }

    // Add ellipsis to the last truncated node if it's text
    if (truncatedNodes.length > 0) {
      const lastNode = truncatedNodes[truncatedNodes.length - 1];
      if (typeof lastNode === 'string') {
        truncatedNodes[truncatedNodes.length - 1] = lastNode + '...';
      } else if (React.isValidElement(lastNode)) {
        // Add ellipsis to the last text node within the element
        const addEllipsisToElement = (element: React.ReactElement<any, any>) => {
          if (!React.isValidElement(element)) return element;

          const children = React.Children.toArray((element as React.ReactElement).props.children);
          if (children.length === 0) return element;

          const lastChild = children[children.length - 1];
          if (typeof lastChild === 'string') {
            children[children.length - 1] = lastChild + '...';
          } else if (React.isValidElement(lastChild)) {
            children[children.length - 1] = addEllipsisToElement(lastChild);
          }

          return React.cloneElement(element, {}, children);
        };

        truncatedNodes[truncatedNodes.length - 1] = addEllipsisToElement(
          lastNode as React.ReactElement
        );
      }
    }

    return {
      truncatedContent: truncatedNodes,
      remainingContent: remainingNodes,
      isTruncated: true,
      truncationInfo
    };
  };

  // Function to reconstruct full content (removing ellipsis and properly joining)
  const reconstructFullContent = (
    truncatedContent: React.ReactNode,
    remainingContent: React.ReactNode | null,
    truncationInfo: any
  ): React.ReactNode[] => {
    if (!truncationInfo) {
      return React.Children.toArray(htmlContent);
    }

    const contentArray = React.Children.toArray(htmlContent);
    let reconstructed = [];

    // Add nodes before truncation point
    for (let i = 0; i < truncationInfo.nodeIndex; i++) {
      reconstructed.push(contentArray[i]);
    }

    // Add the original complete node at truncation point
    reconstructed.push(truncationInfo.originalNode);

    // Add remaining nodes after truncation point
    for (let i = truncationInfo.nodeIndex + 1; i < contentArray.length; i++) {
      reconstructed.push(contentArray[i]);
    }

    return reconstructed;
  };

  const result = processContent();

  return {
    ...result,
    reconstructFullContent: () =>
      reconstructFullContent(
        result.truncatedContent,
        result.remainingContent,
        result.truncationInfo
      )
  };
};
export const filterContent = (content: React.ReactNode[]): React.ReactNode[] => {
  return Array.isArray(content)
    ? content.filter((node) => {
        if (typeof node === 'string') {
          return node.trim() !== ''; // Remove empty strings
        }
        if (React.isValidElement(node)) {
          return React.Children.count((node as React.ReactElement).props.children) > 0; // Keep elements with children
        }
        return false; // Remove other non-valid elements
      })
    : [];
};

// Fetch Looker data
export async function fetchLookerData(
  apiUrl: string = `${process.env.ASSETS_HOST}/seodata/looker-data.json`
): Promise<Record<string, any>> {
  try {
    console.log(`Fetching Looker data from ${apiUrl}...`);
    // Single API call for all data
    const response = await fetch(apiUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch Looker data: ${response.statusText}`);
    }

    const allData = await response.json();
    return allData;
  } catch (error) {
    console.error('Error fetching Looker data:', error);
    throw error;
  }
}

export function processContentWithTokens(
  content: string,
  data: { token: string; value: string }[]
): string {
  // Check if content contains any tokens and process them in one pass
  const tokenRegex = /{{\s*([\w_]+)\s*}}/g;
  // If no tokens in the content, return as-is (avoid unnecessary processing)
  if (!content.includes('{{')) {
    return content;
  }
  if (!Array.isArray(data)) {
    console.warn('Data is not an array.');
    return content;
  }
  // Replace all tokens in one pass
  return content.replace(tokenRegex, (match, tokenName) => {
    let resolvedValue = data.find((item) => item.token === tokenName)?.value ?? null;
    let replacementValue = '';
    if (resolvedValue !== null) {
      if (process.env.ENV_NAME !== 'production' && tokenName.startsWith('avg')) {
        replacementValue = `<span class=\"bg-alert\">${resolvedValue}</span>`;
      } else {
        replacementValue = resolvedValue;
      }
    } else {
      console.warn(`Token ${match} could not be resolved`);
      if (process.env.ENV_NAME !== 'production') {
        replacementValue = `<span class=\"bg-red text-white p-1\">NULL</span>`;
      } else {
        replacementValue = 'NULL';
      }
    }
    return replacementValue;
  });
}
